/**
 * ลงนามรับทราบหนังสือเตือนบนจอ — ฝั่งพนักงาน (/me/letters/:id/sign) · fb#176 TN Riches
 *
 * ลำดับ: เปิด PDF ฉบับจริง → ยืนยันว่าอ่านถึงท้าย → วาดลายเซ็น → ติ๊กข้อความ "ได้รับหนังสือแล้ว
 * (ไม่ใช่ยอมรับข้อกล่าวหา)" → ส่ง · ตัวตนมาจากบัญชีที่ล็อกอิน ไม่ส่ง emp_code
 * โครงเดียวกับ PolicyRead (รับทราบข้อบังคับ) — คนละตาราง คนละเส้น API
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, FileText, ShieldCheck } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { LoadError } from '../components/layout/LoadError';
import { ProtectedPolicyPdf } from '../components/domain/ProtectedPolicyPdf';
import { SignatureSvgPad } from '../components/domain/SignatureSvgPad';
import { useToast } from '../components/ui/toast';
import { useAckMyLetter, useMyLetter } from '../hooks';
import { ApiError } from '../lib/api-client';

function thaiDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function LetterSign() {
  const { id } = useParams<{ id: string }>();
  const letters = useMyLetter(id);
  const ack = useAckMyLetter();
  const toast = useToast();
  const startedAt = useRef<number>(Date.now());
  const [reachedEnd, setReachedEnd] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);

  const row = letters.data;

  useEffect(() => {
    startedAt.current = Date.now();
  }, [id]);

  async function submit() {
    if (!id || !signature) return;
    try {
      const res = await ack.mutateAsync({
        id,
        signature_svg: signature,
        read_seconds: Math.max(0, Math.floor((Date.now() - startedAt.current) / 1000)),
      });
      toast.success(res.already_acked ? 'หนังสือฉบับนี้ลงนามรับทราบไว้แล้ว' : 'บันทึกการลงนามรับทราบแล้ว');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ กรุณาลองใหม่');
    }
  }

  if (letters.isLoading) {
    return (
      <PageLayout title="ลงนามรับทราบ">
        <div className="space-y-4 animate-pulse" aria-label="กำลังโหลด">
          <div className="card p-4"><div className="h-5 w-2/3 bg-gray-100 rounded" /></div>
          <div className="card h-[60vh] bg-gray-100" />
        </div>
      </PageLayout>
    );
  }
  if (letters.error) {
    return (
      <PageLayout title="ลงนามรับทราบ">
        <LoadError error={letters.error} onRetry={() => void letters.refetch()} />
      </PageLayout>
    );
  }
  if (!row) {
    return (
      <PageLayout title="ลงนามรับทราบ">
        <div className="card p-5 text-sm text-gray-600">
          ไม่พบหนังสือฉบับนี้ — อาจถูกยกเลิกแล้ว หรือไม่ใช่หนังสือของคุณ{' '}
          <Link to="/me/letters" className="text-teal underline">กลับไปหนังสือของฉัน</Link>
        </div>
      </PageLayout>
    );
  }

  const canSubmit = reachedEnd && confirmed && Boolean(signature) && !ack.isPending;

  return (
    <PageLayout title="ลงนามรับทราบ">
      <div className="space-y-4 pb-8">
        <section className="card p-4">
          <div className="flex items-start gap-3">
            <FileText className="w-6 h-6 text-brand-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h1 className="font-semibold break-words">{row.title}</h1>
              <p className="text-xs text-gray-500 mt-1">เลขที่ {row.doc_no ?? '—'} · ออกเมื่อ {thaiDateTime(row.issued_at)}</p>
            </div>
          </div>
        </section>

        <section className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="font-semibold">เอกสารฉบับเต็ม</p>
            {row.needs_ack && <p className="text-xs text-gray-500">อ่าน PDF ถึงหน้าสุดท้าย แล้วทำเครื่องหมายยืนยันใต้ตัวเอกสาร</p>}
          </div>
          {/* key เปลี่ยนเมื่อลงนามแล้ว → โหลด PDF ใหม่ให้เห็นลายเซ็นในเอกสาร ไม่ค้างไฟล์เก่า */}
          <ProtectedPolicyPdf
            key={row.ack?.acked_at ?? 'unsigned'}
            endpoint={`/me/letters/${encodeURIComponent(row.id)}/pdf`}
            title={row.title}
            requireEndConfirmation={row.needs_ack}
            reachedEnd={reachedEnd}
            onReachedEnd={() => setReachedEnd(true)}
          />
        </section>

        {row.ack ? (
          <section className="card p-5 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
            <p className="font-semibold text-green-700 mt-2">ลงนามรับทราบหนังสือฉบับนี้แล้ว</p>
            <p className="text-sm text-gray-500 mt-1">
              {row.ack.method === 'paper' ? `ลงนามบนเอกสารฉบับกระดาษ ${row.ack.signed_on ?? ''}` : `บันทึกเมื่อ ${thaiDateTime(row.ack.acked_at)}`}
            </p>
            <Link to="/me/letters" className="btn-secondary inline-flex mt-4 min-h-11">กลับไปหนังสือของฉัน</Link>
          </section>
        ) : !row.needs_ack ? (
          <section className="card p-4 text-sm text-gray-600">หนังสือฉบับนี้ไม่ต้องลงนามรับทราบ</section>
        ) : (
          <section className="card p-4 space-y-4">
            {!reachedEnd && <p className="text-sm text-amber-700">ยังลงนามไม่ได้ กรุณาเลื่อนอ่านเอกสารให้ถึงท้าย</p>}
            {reachedEnd && <SignatureSvgPad onChange={setSignature} />}
            <label className="flex items-start gap-3 min-h-11 text-sm">
              <input
                type="checkbox"
                className="w-5 h-5 mt-0.5 shrink-0"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={!reachedEnd || ack.isPending}
              />
              <span>
                ข้าพเจ้าได้รับและอ่านหนังสือฉบับนี้แล้ว
                <span className="block text-xs text-gray-500 mt-0.5">
                  การลงนามเป็นการรับว่าได้รับหนังสือ มิใช่การยอมรับข้อกล่าวหา — หากต้องการชี้แจง ยื่นเป็นลายลักษณ์อักษรต่อฝ่ายบุคคลได้ภายใน 7 วัน
                </span>
              </span>
            </label>
            <button className="btn-cta w-full min-h-12" onClick={() => void submit()} disabled={!canSubmit}>
              <ShieldCheck className="w-5 h-5" /> {ack.isPending ? 'กำลังบันทึก…' : 'ลงนามรับทราบ'}
            </button>
            {!canSubmit && reachedEnd && !ack.isPending && (
              <p className="text-xs text-amber-700 text-center">
                {!signature ? 'กรุณาวาดลายเซ็นและติ๊กยืนยัน' : 'กรุณาติ๊กยืนยันก่อนกดลงนาม'}
              </p>
            )}
          </section>
        )}
      </div>
    </PageLayout>
  );
}
